import type { OperationReportItem } from '@travel-planner/shared/schemas/operations';
import {
  locationRefSiblingId,
  travellerCount,
} from '@travel-planner/shared/utils';
import { format } from 'date-fns';
import { stripHtml } from '@/shared/lib';
import { getItemLegLocations } from './item-details';
import { toUtcDate } from './office-time';

const DASH = '-';

type Crm = Record<string, unknown>;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

// Pick a readable string out of a translated title (`[{ languageCode, text }]`)
// or a plain string. (3200) Prefer the itinerary's language, then default, then
// en, then any. Returns '' when nothing is readable so callers can fall back.
const resolveI18nText = (
  value: unknown,
  preferLang: string | null | undefined
): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const entries = value as Array<{
      text?: string | null;
      languageCode?: string | null;
      default?: boolean | null;
    }>;
    const lang = preferLang?.toLowerCase();
    const picked =
      (lang
        ? entries.find((e) => e.languageCode?.toLowerCase() === lang && e.text)
        : undefined) ??
      entries.find((e) => e.default && e.text) ??
      entries.find((e) => e.languageCode === 'en' && e.text) ??
      entries.find((e) => e.text);
    if (picked?.text) return picked.text;
  }
  return '';
};

export const reportProductName = (item: OperationReportItem): string =>
  resolveI18nText(item.title, item.languageCode) || item.catalogTitle || DASH;

// The name to show for a row REFERENCED as another service's pickup / drop-off.
// Prefers the live product title over the item's snapshot: the snapshot is a
// per-itinerary copy a consultant may have edited, but when it is the pickup
// label on someone ELSE's service a stale trading name is just a ghost — the
// driver is sent to "Azor Hotel", a property renamed "Octant Ponta Delgada"
// (XT-621). The row's own "Name of product" keeps the snapshot.
export const reportSiblingName = (item: OperationReportItem): string =>
  resolveI18nText(item.productTitle, item.languageCode) ||
  reportProductName(item);

const primarySupplier = (item: OperationReportItem) =>
  item.resolvedSuppliers.find((s) => s.primary) ??
  item.resolvedSuppliers[0] ??
  null;

export type ReportSupplier = {
  name: string;
  email: string;
  phone: string;
  /** The resolved CRM row is soft-deleted, so name/email/phone are a ghost. */
  retired: boolean;
};

// Resolve a supplier's contact email/phone from its `crmInfo` blob (legacy
// `crmInfo.email`/`officePhone`). Returns `null` when absent so callers can
// hide the copy affordance. Shared by the report row and the row copy icons.
export const supplierContact = (
  supplier: { crmInfo?: unknown } | null | undefined
): { email: string | null; phone: string | null } => {
  const crm = (supplier?.crmInfo ?? null) as Crm | null;
  if (!crm) return { email: null, phone: null };
  return {
    email: str(crm.email) ?? str(crm.billingEmail),
    phone:
      str(crm.officePhone) ??
      str(crm.mobilePhone) ??
      str(crm.billingOfficePhone),
  };
};

// The CRM row a supplier resolves to is soft-deleted. The read paths join
// WITHOUT a `deletedOn` filter on purpose (`enrichSuppliersWithCrmInfo` —
// filtering dropped the phone on ~371k live items), so a de-duplicated or
// retired company still resolves. That is right for continuity but wrong to
// print unlabelled: XT-621 had Operations phoning a dead contact off a company
// soft-deleted a year earlier while the live replacement sat in CRM under the
// same name. We cannot pick the successor for them — the de-duplication left no
// forward pointer, and guessing by name would silently swap one supplier's
// contact for another's — so mark it and let the consultant re-point the item.
export const supplierIsRetired = (
  supplier: { crmInfo?: unknown } | null | undefined
): boolean => {
  const crm = (supplier?.crmInfo ?? null) as Crm | null;
  return Boolean(crm && str(crm.deletedOn));
};

// The CRM row is the live truth for a supplier's NAME too, not just its contact
// details. The item's `suppliers` jsonb is a snapshot taken when the service was
// added, so a renamed company kept printing its old trading name beside a
// freshly-joined email/phone (XT-621). Prefer the joined row; fall back to the
// snapshot when the id resolves to nothing (cross-org / hard-deleted).
const supplierDisplayName = (
  supplier: { name?: string; crmInfo?: unknown } | null | undefined
): string | null => {
  const crm = (supplier?.crmInfo ?? null) as Crm | null;
  const live =
    str(crm?.name) ??
    [str(crm?.firstName), str(crm?.lastName)].filter(Boolean).join(' ').trim();
  return (live || null) ?? str(supplier?.name);
};

// Appended to a retired supplier's NAME rather than rendered as a badge: this
// report's whole purpose is being copied to the clipboard and pasted into an
// email, and styling does not survive that. A suffix travels with the text, so
// whoever reads the paste sees the warning too.
const RETIRED_SUFFIX = ' (⚠ supplier archived in CRM — verify contact)';

export const reportSupplier = (item: OperationReportItem): ReportSupplier => {
  const supplier = primarySupplier(item);
  const { email, phone } = supplierContact(supplier);
  const retired = supplierIsRetired(supplier);
  const name = supplierDisplayName(supplier) ?? DASH;
  return {
    name: retired && name !== DASH ? `${name}${RETIRED_SUFFIX}` : name,
    email: email ?? DASH,
    phone: phone ?? DASH,
    retired,
  };
};

export const reportDateHour = (item: OperationReportItem): string =>
  item.startTime
    ? format(toUtcDate(item.startTime), 'd MMM yyyy, HH:mm')
    : DASH;

export const reportTimeRange = (item: OperationReportItem): string => {
  const start = item.startTime
    ? format(toUtcDate(item.startTime), 'HH:mm')
    : DASH;
  const end = item.endTime ? format(toUtcDate(item.endTime), 'HH:mm') : DASH;
  return `${start}/${end}`;
};

export const reportDuration = (item: OperationReportItem): string => {
  if (!(item.startTime && item.endTime)) return DASH;
  const mins = Math.round(
    (toUtcDate(item.endTime).getTime() - toUtcDate(item.startTime).getTime()) /
      60000
  );
  if (mins <= 0) return DASH;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}min`;
  return h ? `${h}h` : `${m}min`;
};

// `details.pickup`/`dropoff` is only a discriminator — either a SIBLING day-row
// reference (a UUID, or `{ kind: 'sibling', siblingId }`) or a typed CUSTOM
// address (`'custom'` / an inline object). The shared parser keeps this in step
// with the Operations list, the api report and the Planner save path (XT-659).
const siblingIdOf = locationRefSiblingId;

// Resolve a sibling item id to the title Planner shows for it. The dialog
// builds this from the itinerary's full item list; null when unknown — which
// includes a pointer at a row the Planner would NOT offer for this item's day,
// so the report never names a location the other surfaces refuse to (XT-659).
export type SiblingTitleResolver = (
  id: string,
  itemStartTime: string | null
) => string | null;

// Pickup / drop-off for a driver or guide, who has to physically GET there —
// so the address leads and the row's name follows in parentheses:
//   "R. Marquês de Subserra 10, 1070-012 Lisboa (Sublime Lisboa)"
// A name alone ("Sublime Lisboa") is not something you can drive to, which is
// what the Planner-parity rendering used to emit for a sibling reference
// (XT-595). Either half alone renders on its own when the other is missing or
// duplicates it. The leg fields are the fallback when the preferred source is
// unavailable.
const withName = (address: string | null, name: string | null): string => {
  const addr = address?.trim() || null;
  const nm = name?.trim() || null;
  if (!addr) return nm ?? DASH;
  if (!nm || nm === addr) return addr;
  return `${addr} (${nm})`;
};

export const reportLocations = (
  item: OperationReportItem,
  resolveSiblingTitle?: SiblingTitleResolver
): { pickup: string; dropoff: string } => {
  const { start, end } = getItemLegLocations(item);
  const d =
    item.details && typeof item.details === 'object'
      ? (item.details as Record<string, unknown>)
      : {};
  const label = (loc: typeof start, ref: unknown): string => {
    const siblingId = siblingIdOf(ref);
    if (siblingId) {
      // The referenced row's name, qualified by whatever address we have for
      // this service's leg (which the save-time sync resolves FROM that row).
      const name =
        resolveSiblingTitle?.(siblingId, item.startTime ?? null) ??
        loc?.name ??
        null;
      const address = loc?.address ?? null;
      if (!name && !address) return DASH;
      return withName(address, name);
    }
    // Custom / typed → the full formatted address, same shape: the short place
    // name ("H10 Duque de Loulé") trails the address it belongs to.
    if (loc) return withName(loc.address, loc.name);
    // Legacy inline custom object with its own address/label and no leg.
    if (ref && typeof ref === 'object') {
      const r = ref as Record<string, unknown>;
      const fa =
        typeof r.formattedAddress === 'string' ? r.formattedAddress : null;
      const lb = typeof r.label === 'string' ? r.label : null;
      return fa ?? lb ?? DASH;
    }
    return DASH;
  };
  return {
    pickup: label(start, d.pickup),
    dropoff: label(end, d.dropoff),
  };
};

const details = (item: OperationReportItem): Crm =>
  item.details && typeof item.details === 'object' ? (item.details as Crm) : {};

// The first leg's `details` jsonb — where the product / Planner Location tab
// writes the meeting point and flight number (the Operations edit-modal instead
// writes them to the item-level `details`), so the report reads both (TP-4059).
const firstLegDetails = (item: OperationReportItem): Crm => {
  const legs = Array.isArray(item.legs) ? item.legs : [];
  const first = legs[0];
  return first && typeof first === 'object' && 'details' in first
    ? (((first as { details?: unknown }).details as Crm) ?? {})
    : {};
};

// meetingPoint / pickup / dropoff are stored either as a plain string or as an
// object (`{ kind: 'custom', label }` / `{ kind: 'sibling', siblingId }`).
// Resolve to a readable label (mirrors the itinerary `locationLabel` helper);
// a sibling reference has no inline label, so it resolves to null.
const locationLabel = (v: unknown): string | null => {
  if (typeof v === 'string') return str(v);
  if (v && typeof v === 'object') {
    const o = v as Crm;
    if (o.kind === 'sibling') return null;
    return (
      str(o.label) ?? str(o.formattedAddress) ?? str(o.title) ?? str(o.city)
    );
  }
  return null;
};

// Meeting point: the Operations edit-modal writes it to the item-level
// `details.meetingPoint`; the product / Planner Location tab writes it to the
// first leg's `details.meetingPoint`. Read both so a value configured on the
// product (e.g. "Arrivals Hall") renders, not just an Operations override
// (TP-4059).
export const reportMeetingPoint = (item: OperationReportItem): string =>
  locationLabel(details(item).meetingPoint) ??
  locationLabel(firstLegDetails(item).meetingPoint) ??
  DASH;

// Restrictions == legacy `supplierNotesText`: the itinerary-level "All supplier
// Notes" (`allSuppliersNotes`, set in itinerary settings) when present,
// otherwise the joined `crmInfo.notes` of every resolved supplier. Mirrors the
// legacy modal composition — the itinerary note wins, per-supplier CRM notes are
// the fallback.
export const reportRestrictions = (item: OperationReportItem): string => {
  const all = str(item.allSuppliersNotes);
  if (all) {
    const text = stripHtml(all).trim();
    if (text.length > 0) return text;
  }
  const notes = item.resolvedSuppliers
    .map((s) => str((s.crmInfo as Crm | null | undefined)?.notes))
    .filter((n): n is string => n != null)
    .map((n) => stripHtml(n).trim())
    .filter((n) => n.length > 0);
  return notes.length > 0 ? notes.join('; ') : DASH;
};

export const reportNote = (item: OperationReportItem): string => {
  const raw =
    str(item.operationDriverNotes) ?? str(details(item).note as string);
  const text = raw ? stripHtml(raw) : '';
  return text || DASH;
};

const firstLegFlightNumber = (item: OperationReportItem): string | null => {
  const legs = Array.isArray(item.legs)
    ? (item.legs as Array<{
        details?: { flightNumber?: unknown; ticketNumber?: unknown } | null;
      }>)
    : [];
  // The Planner Location tab writes the flight number to the leg's
  // `ticketNumber`; older / imported data used `flightNumber`. Read both so a
  // flight number set on the product renders (TP-4059).
  const raw = legs[0]?.details?.ticketNumber ?? legs[0]?.details?.flightNumber;
  return typeof raw === 'string' || typeof raw === 'number'
    ? str(String(raw))
    : null;
};

// Flight number (legacy parity): `details.ticketNumber`, then
// `details.flightNumber`, then the first leg's `details.flightNumber`. For a
// sub-item the parent carries it, so callers pass the resolved parent item.
export const reportFlightNumber = (
  item: OperationReportItem,
  parent?: OperationReportItem | null
): string => {
  const source = item.parentId && parent ? parent : item;
  return (
    str(details(source).ticketNumber as string) ??
    str(details(source).flightNumber as string) ??
    firstLegFlightNumber(source) ??
    DASH
  );
};

export const reportLanguage = (item: OperationReportItem): string =>
  str(item.languageCode)?.toUpperCase() === 'EN'
    ? 'English'
    : (str(item.languageCode) ?? 'English');

// `item.travellers` arrives as the row's EFFECTIVE composition (own → parent
// item → itinerary; see `effectiveTravellersSql`), so this is a plain
// head-count over the shared payload shape.
export const reportTravellerCount = (item: OperationReportItem): number =>
  travellerCount(item.travellers);

// contactInfo entries are "Name:email:phone" strings (legacy parity).
const contactNames = (contactInfo: unknown): string[] => {
  if (!Array.isArray(contactInfo)) return [];
  return contactInfo
    .filter((e): e is string => typeof e === 'string' && e.trim().length > 0)
    .map((e) => e.split(':')[0]?.trim() ?? '')
    .filter((n) => n.length > 0);
};

// Names carried on the travellers payload (`{ people: [{ title }] }`) — the
// v2 modal's final fallback after the lead name and the itinerary contact info
// (v3 had dropped it). Restored so a traveller set on the itinerary still shows
// when it isn't mirrored into the contact-info strings (TP-4059).
const peopleTitles = (item: OperationReportItem): string[] => {
  const payload = item.travellers as { people?: unknown } | null;
  const people = Array.isArray(payload?.people) ? payload.people : [];
  return people
    .map((person) => str((person as { title?: unknown } | null)?.title))
    .filter((name): name is string => name != null);
};

export const reportTravellerNames = (item: OperationReportItem): string => {
  const lead = str(details(item).leadTravellerName as string);
  if (lead) return lead;
  const names = contactNames(item.itineraryTravellersContactInfo);
  if (names.length > 0) return names.join(', ');
  const titles = peopleTitles(item);
  return titles.length > 0 ? titles.join(', ') : DASH;
};

// Customer-facing preview link for the report header (legacy parity).
export const itineraryPreviewLink = (itineraryId: string | null): string => {
  if (!itineraryId) return DASH;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/preview/${itineraryId}/itinerary?view=traveller`;
};

export type ReportGroup = {
  dayKey: string;
  label: string;
  items: OperationReportItem[];
};

// Group items by calendar day (UTC), preserving chronological order.
export const groupReportItemsByDay = (
  items: OperationReportItem[]
): ReportGroup[] => {
  const buckets = new Map<string, OperationReportItem[]>();
  for (const item of items) {
    const key = item.startTime ? item.startTime.slice(0, 10) : 'undated';
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, dayItems]) => ({
      dayKey,
      label:
        dayKey === 'undated'
          ? 'Undated'
          : format(toUtcDate(`${dayKey} 00:00:00`), 'd MMMM yyyy'),
      items: dayItems,
    }));
};

const groupReportItemsByKey = (
  items: OperationReportItem[],
  key: (item: OperationReportItem) => string
): ReportGroup[] => {
  const buckets = new Map<string, OperationReportItem[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = buckets.get(k);
    if (bucket) bucket.push(item);
    else buckets.set(k, [item]);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, dayItems]) => ({ dayKey, label: dayKey, items: dayItems }));
};

const reportSupplierName = (item: OperationReportItem): string => {
  const supplier = primarySupplier(item);
  if (!supplier) return 'No supplier';
  // Group by the same name the card prints, or Per-supplier grouping splits one
  // company into a stale-name bucket and a live-name bucket.
  return supplierDisplayName(supplier) ?? 'No supplier';
};

// (2932) Copy-report grouping modes shared by the transport + guide dialogs:
// Per day / Per supplier / Per city-region.
export type ReportGroupMode = 'day' | 'supplier' | 'city';

export const groupReportItems = (
  items: OperationReportItem[],
  mode: ReportGroupMode
): ReportGroup[] => {
  if (mode === 'day') return groupReportItemsByDay(items);
  if (mode === 'supplier') {
    return groupReportItemsByKey(items, reportSupplierName);
  }
  return groupReportItemsByKey(
    items,
    (item) => item.city || item.country || 'Unknown'
  );
};
